import { useUser } from "~/stores/user";
import { type Pinia } from "pinia";
import { getDefaultRoute } from "@/utils/defaultRoute";
import { useEndpointPermissions } from "@/composables/useEndpointPermissions";
import { useFollowedCompanyCount } from "@/composables/useFollowedCompanyCount";
import { useSavedCompanyCount } from "@/composables/useSavedCompanyCount";
import { useSavedFilingCount } from "@/composables/useSavedFilingCount";
import { hasMonitoringAccess } from "@/utils/monitoringAccess";
import { record } from "~/composables/store";
import type { SubscriptionAccessStatus } from "~/utils/subscriptionAccess";

export default defineNuxtRouteMiddleware(async (to, from) => {
	{
		if (to.path === "/onboarding" || to.path.includes("auth") || to.path.includes("api") || to.path.startsWith("/reset-password") || to.path.startsWith("/create-password")) {
			return;
		}

		const cookie = useCookie("__session");
		//console.log("auth.global", to.path, cookie.value);

		// If user has no cookie session and visiting any page other than login, redirect to login
		if (!cookie.value) {
			if (to.path !== "/") {
				return navigateTo("/");
			}
			return;
		}

		// If user has a cookie session, check if it is valid
		const app = useNuxtApp();
		const userStore = useUser(app.$pinia as Pinia);
		try {
			const access = await record<SubscriptionAccessStatus>("billing/access-status");
			if (access.payment_required) {
				if (to.path !== "/payment-required") return navigateTo("/payment-required");
				return;
			}
		} catch {
			return navigateTo("/auth/logout", { external: true });
		}
		if (process.client && userStore.user && to.path !== "/" && to.path !== "/payment-required") return;

		// The sidebar gates Recommendations, Saved and Monitoring on the user's
		// endpoint permissions and company counts. Load them during the server
		// render so the nav is complete on first paint. The monitoring count
		// waits for the user permission; the other loads can run in parallel.
		const { loadEndpointPermissions } = useEndpointPermissions();
		const { loadFollowedCompanyCount } = useFollowedCompanyCount();
		const { loadSavedCompanyCount } = useSavedCompanyCount();
		const { loadSavedFilingCount } = useSavedFilingCount();
		const userLoad = userStore.load();
		const [userResult] = await Promise.allSettled([
			userLoad,
			loadEndpointPermissions(),
			userLoad.then(() => hasMonitoringAccess(userStore.user) ? loadFollowedCompanyCount() : 0),
			loadSavedCompanyCount(),
			userLoad.then(() => loadSavedFilingCount()),
		]);
		if (userResult.status === "rejected") {
			return navigateTo("/auth/logout", { external: true });
		}

		// If user is visiting login while authenticated, redirect to first accessible page
		if (to.path === "/" || to.path === "/payment-required") {
			return navigateTo(getDefaultRoute(userStore.user));
		}
	}
});
